import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useList, type CrudFilters } from "@refinedev/core";
import { Link } from "react-router-dom";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";

import type { ArchivedTaskResponse, TaskResponse } from "../../../api-client";

export interface TasksTableProps {
  campaignId?: string;
  userId?: string;
  archived?: boolean;
  showCampaignLink?: boolean;
  showUserLink?: boolean;
}

type TaskRecord = TaskResponse | ArchivedTaskResponse;

export const TasksTable: React.FC<TasksTableProps> = ({
  campaignId,
  userId,
  archived = false,
  showCampaignLink = false,
  showUserLink = false,
}) => {
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filtersState, setFiltersState] = useState<{
    status: TaskRecord["status"] | "all";
    dateRange: [Dayjs, Dayjs] | null;
    errorsOnly: boolean;
  }>({
    status: "all",
    dateRange: null,
    errorsOnly: false,
  });

  useEffect(() => {
    setCurrent(1);
  }, [campaignId, userId, archived]);

  const endpoint = useMemo(() => {
    if (campaignId && !archived) {
      return `/api/tasks/by-campaign/${campaignId}`;
    }

    if (campaignId && archived) {
      return `/api/tasks/archived/by-campaign/${campaignId}`;
    }

    if (userId && !archived) {
      return `/api/tasks/by-user/${userId}`;
    }

    if (userId && archived) {
      return `/api/tasks/archived/by-user/${userId}`;
    }

    return undefined;
  }, [archived, campaignId, userId]);

  const dateField = archived ? "executed_at" : "created_at";

  const filters = useMemo<CrudFilters>(() => {
    const computedFilters: CrudFilters = [];

    if (filtersState.status !== "all") {
      computedFilters.push({
        field: "status",
        operator: "eq",
        value: filtersState.status,
      });
    }

    if (filtersState.errorsOnly) {
      computedFilters.push({
        field: "errors_only",
        operator: "eq",
        value: true,
      });
    }

    if (filtersState.dateRange) {
      computedFilters.push(
        {
          field: dateField,
          operator: "gte",
          value: filtersState.dateRange[0].startOf("day").toISOString(),
        },
        {
          field: dateField,
          operator: "lte",
          value: filtersState.dateRange[1].endOf("day").toISOString(),
        },
      );
    }

    return computedFilters;
  }, [dateField, filtersState]);

  const tasksQuery = useList<TaskRecord>({
    resource: "tasks",
    meta: {
      url: endpoint,
      archived,
    },
    filters,
    pagination: archived
      ? {
          current,
          pageSize,
          mode: "server",
        }
      : { mode: "off" },
    queryOptions: {
      enabled: Boolean(endpoint),
    },
  });

  const tasks = useMemo(
    () => tasksQuery.result?.data ?? [],
    [tasksQuery.result?.data],
  );
  const total = tasksQuery.result?.total ?? 0;
  const isError = tasksQuery.query.isError;
  const handleRetry = () => tasksQuery.query.refetch?.();

  const filteredTasks = useMemo(() => {
    if (archived) {
      return tasks;
    }

    return tasks.filter((task) => {
      const matchesStatus =
        filtersState.status === "all" || task.status === filtersState.status;

      const matchesErrors = !filtersState.errorsOnly || Boolean(task.error);

      const matchesDate = (() => {
        if (!filtersState.dateRange) {
          return true;
        }

        const [start, end] = filtersState.dateRange;
        const taskDate = task[dateField];

        if (!taskDate) {
          return false;
        }

        const dateValue = new Date(taskDate).getTime();

        return (
          dateValue >= start.startOf("day").valueOf() &&
          dateValue <= end.endOf("day").valueOf()
        );
      })();

      return matchesStatus && matchesErrors && matchesDate;
    });
  }, [archived, dateField, filtersState.dateRange, filtersState.errorsOnly, filtersState.status, tasks]);

  const hasActiveFilters = useMemo(
    () =>
      filtersState.status !== "all" ||
      filtersState.dateRange !== null ||
      filtersState.errorsOnly,
    [filtersState.dateRange, filtersState.errorsOnly, filtersState.status],
  );

  const { RangePicker } = DatePicker;

  const errorAlert = isError ? (
    <Alert
      type="error"
      showIcon
      icon={<ExclamationCircleOutlined />}
      message="Не удалось загрузить задачи"
      description="Проверьте подключение или повторите попытку."
      action={
        <Button size="small" icon={<ReloadOutlined />} onClick={handleRetry}>
          Повторить
        </Button>
      }
      style={{ marginBottom: 16 }}
    />
  ) : null;

  const effectiveDataSource = isError
    ? []
    : archived
      ? tasks
      : filteredTasks;

  const emptyState = useMemo(() => {
    if (!hasActiveFilters) {
      const title = archived
        ? "Архивных задач пока нет"
        : "Задачи пока не созданы";
      const description = archived
        ? "Архив появится после выполнения задач кампании"
        : "Создайте задачу вручную или активируйте кампанию, чтобы запустить отправку";

      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div>{title}</div>
              <Typography.Text type="secondary" style={{ margin: 0 }}>
                {description}
              </Typography.Text>
            </div>
          }
        />
      );
    }

    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div>Нет задач, соответствующих выбранным фильтрам</div>
            <Typography.Text type="secondary" style={{ margin: 0 }}>
              Измените параметры или очистите фильтры, чтобы увидеть задачи
            </Typography.Text>
          </div>
        }
      >
        <Button
          type="primary"
          onClick={() =>
            setFiltersState({ status: "all", dateRange: null, errorsOnly: false })
          }
        >
          Сбросить фильтры
        </Button>
      </Empty>
    );
  }, [archived, hasActiveFilters]);

  const columns: ColumnsType<TaskRecord> = [
    {
      title: "Пользователь",
      dataIndex: "user_id",
      key: "user_id",
      render: (value: string, record: TaskRecord) => {
        if (showUserLink) {
          // user_id имеет формат "user:telegram_id", извлекаем только telegram_id для ссылки
          const telegramId = record.user_id.replace("user:", "");
          return <Link to={`/users/show/${telegramId}`}>{record.user_id}</Link>;
        }

        return value;
      },
    },
    {
      title: "Кампания",
      dataIndex: "campaign_id",
      key: "campaign_id",
      render: (value: string, record: TaskRecord) => {
        if (showCampaignLink) {
          return (
            <Link to={`/campaigns/show/${record.campaign_id}`}>
              {record.campaign_id}
            </Link>
          );
        }

        return value;
      },
    },
    {
      title: "Текст сообщения",
      dataIndex: "message_text",
      key: "message_text",
      render: (value?: string) => {
        const messageText = value ?? "";
        const truncatedMessage =
          messageText.length > 50
            ? `${messageText.slice(0, 50)}…`
            : messageText;

        return (
          <Tooltip title={messageText}>
            <Typography.Text ellipsis>{truncatedMessage}</Typography.Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Статус",
      dataIndex: "status",
      key: "status",
      render: (value: TaskRecord["status"]) => {
        const tagColor: string | undefined = {
          pending: "default",
          processing: "processing",
          done: "success",
          failed: "error",
        }[value];

        const statusAriaLabels: Record<TaskRecord["status"], string> = {
          pending: "Статус: В ожидании",
          processing: "Статус: В обработке",
          done: "Статус: Завершено",
          failed: "Статус: Ошибка",
        };

        return <Tag color={tagColor} aria-label={statusAriaLabels[value]}>{value}</Tag>;
      },
    },
    {
      title: "Повторы",
      dataIndex: "retries",
      key: "retries",
    },
    {
      title: "Ошибка",
      dataIndex: "error",
      key: "error",
      render: (value?: string | null) => {
        if (!value) {
          return "-";
        }

        return (
          <Tooltip title={value}>
            <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />
          </Tooltip>
        );
      },
    },
    {
      title: "Создано",
      dataIndex: "created_at",
      key: "created_at",
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        {errorAlert}
        <div
          style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Select<TaskRecord["status"] | "all">
            allowClear={false}
            style={{ width: 220 }}
            options={[
              { label: "Все статусы", value: "all" },
              { label: "В ожидании", value: "pending" },
              { label: "В обработке", value: "processing" },
              { label: "Завершено", value: "done" },
              { label: "Ошибка", value: "failed" },
            ]}
            placeholder="Фильтр по статусу"
            value={filtersState.status}
            onChange={(value) =>
              setFiltersState((previous) => ({ ...previous, status: value }))
            }
          />
          <RangePicker
            value={filtersState.dateRange}
            onChange={(dates) =>
              setFiltersState((previous) => ({
                ...previous,
                dateRange: dates as [Dayjs, Dayjs] | null,
              }))
            }
            allowClear
            format="DD.MM.YYYY"
            placeholder={["Дата начала", "Дата окончания"]}
          />
          <Checkbox
            checked={filtersState.errorsOnly}
            onChange={(event) =>
              setFiltersState((previous) => ({
                ...previous,
                errorsOnly: event.target.checked,
              }))
            }
          >
            Показывать только с ошибками
          </Checkbox>
          {hasActiveFilters ? (
            <Button
              onClick={() =>
                setFiltersState({
                  status: "all",
                  dateRange: null,
                  errorsOnly: false,
                })
              }
            >
              Очистить фильтры
            </Button>
          ) : null}
        </div>
      </div>
      <Table
        dataSource={effectiveDataSource}
        columns={columns}
        rowKey="id"
        loading={tasksQuery.query.isLoading}
        locale={emptyState ? { emptyText: emptyState } : undefined}
        pagination={
          archived
            ? {
                current,
                pageSize,
                total,
                onChange: (page, size) => {
                  setCurrent(page);
                  setPageSize(size);
                },
              }
            : false
        }
        data-campaign-id={campaignId}
        data-user-id={userId}
        data-archived={archived}
        data-show-campaign-link={showCampaignLink}
        data-show-user-link={showUserLink}
      />
    </div>
  );
};
